import * as React from "react";

import * as m from "../../core/model/model";
import { useActiveBankConnection } from "../useActiveBankConnection/useActiveBankConnection";
import { usePolling } from "../usePolling/usePolling";

export const useSveaAuthentication = (
	getLastSveaActivityDate: () => Promise<Date | undefined>,
	onLogout: () => void,
	onAboutToExpire: (secondsLeft: number) => void,
) => {
	const { hasActiveBusinessAccountWithExternalSystem } = useActiveBankConnection();
	const [isProtecting, setIsProtecting] = React.useState(false);
	const checkActivityCookie = React.useCallback(() => getLastSveaActivityDate(), [getLastSveaActivityDate]);
	const idleMillisecondsToShowLogoutNeededSoon = 4 * 60 * 1000;
	const idleMillisecondsToLogOut = 5 * 60 * 1000;

	const { isPolling, startPolling, stopPolling } = usePolling({
		poll: checkActivityCookie,
		options: {
			effect: async lastActivity => {
				if (!lastActivity) {
					return; //ME: It's a bit odd that we do nothing if we have no last activity
				}
				const idleTime = new Date().getTime() - lastActivity.getTime();
				if (idleTime >= idleMillisecondsToLogOut) {
					//ME: Too late, need to log out
					setIsProtecting(false);
					stopPolling();
					onLogout();
				} else if (idleTime >= idleMillisecondsToShowLogoutNeededSoon) {
					//ME: Almost too late, let them extend
					onAboutToExpire(idleMillisecondsToLogOut - idleTime);
				}
			},
			stopCondition: () => false,
			interval: 1000, // If this changes we need to add some more logic to the timer that shows in the UIs
		},
	});

	const hasSveaBusinessAccount = hasActiveBusinessAccountWithExternalSystem(m.Entities.Bank.ExternalSystem.Svea);

	React.useEffect(() => {
		if (isProtecting && !isPolling && hasSveaBusinessAccount) {
			startPolling();
		} else if (!isProtecting && isPolling) {
			stopPolling();
		}

		return () => {
			if (isPolling) {
				stopPolling();
			}
		};
	}, [hasSveaBusinessAccount, isPolling, isProtecting, startPolling, stopPolling]);

	const startProtecting = React.useCallback(() => {
		setIsProtecting(true);
	}, []);

	const stopProtecting = React.useCallback(() => {
		setIsProtecting(false);
	}, []);

	return {
		startProtecting,
		stopProtecting,
	};
};
